import { Component, OnInit, OnDestroy, ChangeDetectorRef, inject } from '@angular/core';
import { Subscription } from 'rxjs';
import { RegisterClass } from '@memberjunction/global';
import { BaseDashboard, DeveloperModeService } from '@memberjunction/ng-shared';
import { ResourceData, MJCompanyIntegrationEntity } from '@memberjunction/core-entities';
import { RunView, Metadata } from '@memberjunction/core';
import { GraphQLDataProvider } from '@memberjunction/graphql-dataprovider';
import {
    marketingBlogEntity, marketingBlogStatusEntity, marketingContentCalendarEntity,
    marketingContentPlanEntity, marketingContentPlanItemEntity, marketingContentFeedEntity,
    marketingContentTemplateEntity, marketingContentStrategyEntity, marketingContentStrategyPillarEntity,
    marketingChannelConfigurationEntity, marketingExperimentEntity, marketingAgentLearningEntity,
    marketingContentTypeEntity, marketingContentStatusEntity, marketingContentSourceEntity,
    marketingNewsletterEntity, marketingNewsletterBlogEntity,
    marketingSocialPostEntity
} from 'mj_generatedentities';
import {
    CMOView, ReviewFilter, CalendarViewMode,
    AgentStepEnhanced, FlyoutState, NotePopoverState,
    MarketingScheduledJob
} from '../../types';

@Component({
    standalone: false,
    selector: 'mkt-cmo-dashboard',
    templateUrl: './cmo-dashboard.component.html',
    styleUrls: ['./cmo-dashboard.component.css']
})
@RegisterClass(BaseDashboard, 'CMODashboard')
export class CMODashboardComponent extends BaseDashboard implements OnInit, OnDestroy {
    private cdr = inject(ChangeDetectorRef);
    private devModeService = inject(DeveloperModeService);
    private devModeSub?: Subscription;

    // ─── View State ────────────────────────────────
    activeView: CMOView = 'review-queue';
    calendarViewMode: CalendarViewMode = 'month';
    calendarDate: Date = new Date();
    reviewFilter: ReviewFilter = 'needs-review';
    settingsOpen: boolean = false;
    sidebarCollapsed: boolean = false;
    isDeveloperMode = false;

    // ─── Company ─────────────────────────────────────
    defaultCompanyId: string = '';
    companies: any[] = [];

    // ─── Lookup Data ─────────────────────────────────
    contentTypes: marketingContentTypeEntity[] = [];
    contentStatuses: marketingContentStatusEntity[] = [];
    blogStatuses: marketingBlogStatusEntity[] = [];

    // ─── Company-Scoped Data ─────────────────────────
    brandConfigRaw: any = null;
    channelConfigs: marketingChannelConfigurationEntity[] = [];
    strategy: marketingContentStrategyEntity | null = null;
    strategyPillars: marketingContentStrategyPillarEntity[] = [];
    contentPlan: marketingContentPlanEntity | null = null;
    contentPlanItems: marketingContentPlanItemEntity[] = [];
    calendarEntries: marketingContentCalendarEntity[] = [];
    contentFeeds: marketingContentFeedEntity[] = [];
    contentTemplates: marketingContentTemplateEntity[] = [];
    blogs: marketingBlogEntity[] = [];
    contentSources: marketingContentSourceEntity[] = [];
    experiments: marketingExperimentEntity[] = [];
    agentLearnings: marketingAgentLearningEntity[] = [];
    newsletters: marketingNewsletterEntity[] = [];
    socialPosts: marketingSocialPostEntity[] = [];
    flyoutNewsletterBlogs: marketingBlogEntity[] = [];

    // ─── Agent State ─────────────────────────────────
    isRunning: boolean = false;
    enhancedSteps: AgentStepEnhanced[] = [];
    runOutputBlogs: marketingBlogEntity[] = [];
    private pollingTimer: any = null;
    private agentStartTime: number = 0;
    private cmoAgentId: string | null = null;
    currentRunId: string | null = null;

    // ─── Flyout State ────────────────────────────────
    flyout: FlyoutState = { open: false, blogId: null, calendarEntryId: null, newsletterId: null, socialPostId: null };
    flyoutBlog: marketingBlogEntity | null = null;
    blogRevisions: any[] = [];

    // ─── Note Popover State ──────────────────────────
    notePopover: NotePopoverState = { open: false, date: null, x: 0, y: 0 };
    noteTitle: string = '';
    noteText: string = '';
    noteContentTypeId: string = '';

    // ─── Calendar Entry Edit State ──────────────────────
    editingCalendarEntry: marketingContentCalendarEntity | null = null;
    editCalendarTitle: string = '';
    editCalendarNotes: string = '';
    editCalendarDate: string = '';

    // ─── Integrations / Social Platforms ────────────
    integrations: { ID: string; Name: string; Description: string }[] = [];
    companyIntegrations: { ID: string; CompanyID: string; IntegrationID: string; Integration: string; IsActive: boolean; AccessToken: string | null; APIKey: string | null }[] = [];
    socialPlatforms: { ID: string; Name: string }[] = [];

    // ─── Scheduled Jobs (Marketing only) ────────────
    scheduledJobs: MarketingScheduledJob[] = [];
    /** Per-job state for the Run Now button: 'idle' | 'running' | 'queued' | 'error' */
    jobRunState: Record<string, 'idle' | 'running' | 'queued' | 'error'> = {};
    /** Per-job last result message (success or error) */
    jobRunMessage: Record<string, string> = {};

    // ─── Data Loading State ──────────────────────────
    isLoading: boolean = true;

    // ─── Save State ──────────────────────────────────
    saveStatus: Record<string, 'idle' | 'saving' | 'saved' | 'error'> = {};

    // ─── Computed (review queue) ─────────────────────
    get needsReviewCount(): number {
        return this.blogs.filter(b => this.isBlogDraft(b)).length;
    }

    get scheduledCount(): number {
        return this.blogs.filter(b => this.isBlogReady(b)).length;
    }

    get publishedCount(): number {
        return this.blogs.filter(b => this.getBlogStatusName(b.StatusID).toLowerCase() === 'published').length;
    }

    get newsletterCount(): number {
        return this.newsletters.length;
    }

    get draftNewsletterCount(): number {
        return this.newsletters.filter(n => this.getContentStatusName(n.ContentStatusID).toLowerCase() === 'draft').length;
    }

    get socialPostCount(): number {
        return this.socialPosts.length;
    }

    get draftSocialPostCount(): number {
        return this.socialPosts.filter(p => this.getContentStatusName(p.ContentStatusID).toLowerCase() === 'draft').length;
    }

    get filteredBlogs(): marketingBlogEntity[] {
        switch (this.reviewFilter) {
            case 'needs-review':
                return this.blogs.filter(b => this.isBlogDraft(b));
            case 'scheduled':
                return this.blogs.filter(b => this.isBlogReady(b));
            case 'all':
            default:
                return this.blogs;
        }
    }

    // ─── Lifecycle ───────────────────────────────────

    protected override initDashboard(): void {
        this.NotifyLoadComplete();
    }

    async GetResourceDisplayName(_data: ResourceData): Promise<string> {
        return 'AI CMO';
    }

    async loadData(): Promise<void> {
        await this.loadAllData();
    }

    override async ngOnInit(): Promise<void> {
        await super.ngOnInit();
        this.devModeSub = this.devModeService.IsEnabled$.subscribe(enabled => {
            this.isDeveloperMode = enabled;
            this.cdr.detectChanges();
        });
        // Restore sidebar collapsed state from localStorage
        const savedCollapsed = localStorage.getItem('cmo-sidebar-collapsed');
        if (savedCollapsed === 'true') this.sidebarCollapsed = true;
        await this.loadAllData();
    }

    override ngOnDestroy(): void {
        this.stopPolling();
        this.devModeSub?.unsubscribe();
        super.ngOnDestroy();
    }

    // ─── Data Loading ────────────────────────────────

    async onCompanyChange(): Promise<void> {
        await this.loadAllData();
    }

    private async loadAllData(): Promise<void> {
        this.isLoading = true;
        this.cdr.detectChanges();

        try {
            const rv = new RunView();
            const cid = this.defaultCompanyId;

            const now = new Date();
            const weekAgo = new Date(now);
            weekAgo.setDate(weekAgo.getDate() - 7);
            const eightWeeksOut = new Date(now);
            eightWeeksOut.setDate(eightWeeksOut.getDate() + 56); // 8 weeks for full month view
            const weekAgoStr = weekAgo.toISOString().split('T')[0];
            const eightWeeksStr = eightWeeksOut.toISOString().split('T')[0];

            const queries: any[] = [
                /* 0 */ { EntityName: 'MJ: Companies', ResultType: 'simple', Fields: ['ID', 'Name'], MaxRows: 50 },
                /* 1 */ { EntityName: 'Content Types', ExtraFilter: 'IsActive=1', ResultType: 'entity_object' },
                /* 2 */ { EntityName: 'Content Status', ResultType: 'entity_object' },
                /* 3 */ { EntityName: 'Blog Status', ExtraFilter: 'IsActive=1', ResultType: 'entity_object' },
            ];

            if (cid) {
                queries.push(
                    /* 4  */ { EntityName: 'Brand Configurations', ExtraFilter: `CompanyID='${cid}' AND IsActive=1`, ResultType: 'entity_object', MaxRows: 1 },
                    /* 5  */ { EntityName: 'Channel Configurations', ExtraFilter: `CompanyID='${cid}'`, ResultType: 'entity_object' },
                    /* 6  */ { EntityName: 'Content Strategies', ExtraFilter: `CompanyID='${cid}' AND IsActive=1`, ResultType: 'entity_object', MaxRows: 1 },
                    /* 7  */ { EntityName: 'Content Strategy Pillars', ExtraFilter: `ContentStrategyID IN (SELECT ID FROM [marketing].[vwContentStrategies] WHERE CompanyID='${cid}' AND IsActive=1)`, OrderBy: 'Priority ASC', ResultType: 'entity_object' },
                    /* 8  */ { EntityName: 'Content Plans', ExtraFilter: `CompanyID='${cid}' AND IsActive=1`, ResultType: 'entity_object', MaxRows: 1 },
                    /* 9  */ { EntityName: 'Content Plan Items', ExtraFilter: `ContentPlanID IN (SELECT ID FROM [marketing].[vwContentPlans] WHERE CompanyID='${cid}' AND IsActive=1) AND IsActive=1`, ResultType: 'entity_object' },
                    /* 10 */ { EntityName: 'Content Calendars', ExtraFilter: `CompanyID='${cid}' AND PlannedDate >= '${weekAgoStr}' AND PlannedDate <= '${eightWeeksStr}'`, OrderBy: 'PlannedDate ASC', ResultType: 'entity_object' },
                    /* 11 */ { EntityName: 'Content Feeds', ExtraFilter: `CompanyID='${cid}'`, ResultType: 'entity_object' },
                    /* 12 */ { EntityName: 'Content Templates', ExtraFilter: `CompanyID='${cid}' AND IsActive=1`, ResultType: 'entity_object' },
                    /* 13 */ { EntityName: 'Blogs', ExtraFilter: `CompanyID='${cid}'`, OrderBy: '__mj_CreatedAt DESC', ResultType: 'entity_object', MaxRows: 50 },
                    /* 14 */ { EntityName: 'Content Sources', ExtraFilter: `CompanyID='${cid}'`, OrderBy: '__mj_CreatedAt DESC', ResultType: 'entity_object', MaxRows: 10 },
                    /* 15 */ { EntityName: 'Experiments', ExtraFilter: `CompanyID='${cid}'`, OrderBy: '__mj_CreatedAt DESC', ResultType: 'entity_object', MaxRows: 10 },
                    /* 16 */ { EntityName: 'Agent Learnings', ExtraFilter: `CompanyID='${cid}'`, OrderBy: 'DateRecorded DESC', ResultType: 'entity_object', MaxRows: 10 },
                    /* 17 */ { EntityName: 'Newsletters', ExtraFilter: `CompanyID='${cid}'`, OrderBy: '__mj_CreatedAt DESC', ResultType: 'entity_object', MaxRows: 20 },
                    /* 18 */ { EntityName: 'Social Posts', ExtraFilter: `CompanyID='${cid}'`, OrderBy: '__mj_CreatedAt DESC', ResultType: 'entity_object', MaxRows: 50 },
                /* 19 */ {
                    EntityName: 'MJ: Scheduled Jobs',
                    ExtraFilter: "Name LIKE 'Marketing -%'",
                    OrderBy: 'Name ASC',
                    ResultType: 'simple',
                    Fields: ['ID', 'Name', 'Description', 'CronExpression', 'Status', 'LastRunAt', 'NextRunAt', 'Type'],
                },
                /* 20 */ { EntityName: 'Social Platforms', ResultType: 'simple', Fields: ['ID', 'Name'] },
                /* 21 */ { EntityName: 'MJ: Integrations', OrderBy: 'Name ASC', ResultType: 'simple', Fields: ['ID', 'Name', 'Description'] },
                /* 22 */ {
                    EntityName: 'MJ: Company Integrations',
                    ExtraFilter: `CompanyID='${cid}'`,
                    ResultType: 'simple',
                    Fields: ['ID', 'CompanyID', 'IntegrationID', 'Integration', 'IsActive'],
                },
                );
            }

            const results = await rv.RunViews(queries);

            if (results[0].Success) {
                this.companies = results[0].Results;
                if (!this.defaultCompanyId && this.companies.length > 0) {
                    this.defaultCompanyId = this.companies[0].ID;
                    this.isLoading = false;
                    await this.loadAllData();
                    return;
                }
            }
            if (results[1].Success) this.contentTypes = results[1].Results as marketingContentTypeEntity[];
            if (results[2].Success) this.contentStatuses = results[2].Results as marketingContentStatusEntity[];
            if (results[3].Success) this.blogStatuses = results[3].Results as marketingBlogStatusEntity[];

            if (cid && results.length > 4) {
                if (results[4].Success) this.brandConfigRaw = results[4].Results?.[0] || null;
                if (results[5].Success) this.channelConfigs = results[5].Results as marketingChannelConfigurationEntity[];
                if (results[6].Success) this.strategy = (results[6].Results?.[0] as marketingContentStrategyEntity) || null;
                if (results[7].Success) this.strategyPillars = results[7].Results as marketingContentStrategyPillarEntity[];
                if (results[8].Success) this.contentPlan = (results[8].Results?.[0] as marketingContentPlanEntity) || null;
                if (results[9].Success) this.contentPlanItems = results[9].Results as marketingContentPlanItemEntity[];
                if (results[10].Success) this.calendarEntries = results[10].Results as marketingContentCalendarEntity[];
                if (results[11].Success) this.contentFeeds = results[11].Results as marketingContentFeedEntity[];
                if (results[12].Success) this.contentTemplates = results[12].Results as marketingContentTemplateEntity[];
                if (results[13].Success) this.blogs = results[13].Results as marketingBlogEntity[];
                if (results[14].Success) this.contentSources = results[14].Results as marketingContentSourceEntity[];
                if (results[15].Success) this.experiments = results[15].Results as marketingExperimentEntity[];
                if (results[16].Success) this.agentLearnings = results[16].Results as marketingAgentLearningEntity[];
                if (results[17]?.Success) this.newsletters = results[17].Results as marketingNewsletterEntity[];
                if (results[18]?.Success) this.socialPosts = results[18].Results as marketingSocialPostEntity[];
                if (results[19]?.Success) {
                    this.scheduledJobs = (results[19].Results as MarketingScheduledJob[]) || [];
                }
                if (results[20]?.Success) this.socialPlatforms = (results[20].Results as { ID: string; Name: string }[]) || [];
                if (results[21]?.Success) this.integrations = (results[21].Results as { ID: string; Name: string; Description: string }[]) || [];
                if (results[22]?.Success) this.companyIntegrations = (results[22].Results as typeof this.companyIntegrations) || [];
            }
        } catch (err) {
            console.error('CMO Dashboard: Failed to load data', err);
        } finally {
            this.isLoading = false;
            this.cdr.detectChanges();
        }
    }

    // ─── Calendar Events ─────────────────────────────

    onCalendarEntryClicked(entry: marketingContentCalendarEntity): void {
        if (entry.BlogID) {
            const blog = this.blogs.find(b => b.ID === entry.BlogID);
            if (blog) {
                this.flyout = { open: true, blogId: blog.ID, calendarEntryId: entry.ID, newsletterId: null, socialPostId: null };
                this.flyoutBlog = blog;
                this.cdr.detectChanges();
            }
        }
    }

    onCalendarEntryEdit(entry: marketingContentCalendarEntity): void {
        this.editingCalendarEntry = entry;
        this.editCalendarTitle = entry.Title || '';
        this.editCalendarNotes = entry.Notes || '';
        this.editCalendarDate = entry.PlannedDate ? new Date(entry.PlannedDate).toISOString().split('T')[0] : '';
        this.cdr.detectChanges();
    }

    async saveCalendarEntryEdit(): Promise<void> {
        if (!this.editingCalendarEntry) return;
        this.editingCalendarEntry.Title = this.editCalendarTitle;
        this.editingCalendarEntry.Notes = this.editCalendarNotes;
        if (this.editCalendarDate) {
            this.editingCalendarEntry.PlannedDate = new Date(this.editCalendarDate);
        }
        await this.editingCalendarEntry.Save();
        this.editingCalendarEntry = null;
        this.cdr.detectChanges();
    }

    async deleteCalendarEntry(): Promise<void> {
        if (!this.editingCalendarEntry) return;
        await this.editingCalendarEntry.Delete();
        this.calendarEntries = this.calendarEntries.filter(e => e.ID !== this.editingCalendarEntry!.ID);
        this.editingCalendarEntry = null;
        this.cdr.detectChanges();
    }

    closeCalendarEntryEdit(): void {
        this.editingCalendarEntry = null;
        this.cdr.detectChanges();
    }

    onCalendarDateClicked(date: Date): void {
        this.notePopover = { open: true, date, x: 400, y: 200 };
        this.noteTitle = '';
        this.noteText = '';
        this.noteContentTypeId = '';
        this.cdr.detectChanges();
    }

    async onEntryRescheduled(event: { entry: marketingContentCalendarEntity, newDate: Date }): Promise<void> {
        event.entry.PlannedDate = event.newDate;
        const result = await event.entry.Save();
        if (result) {
            this.cdr.detectChanges();
        }
    }

    // ─── Note Popover ────────────────────────────────

    closeNotePopover(): void {
        this.notePopover = { open: false, date: null, x: 0, y: 0 };
        this.cdr.detectChanges();
    }

    async saveNote(): Promise<void> {
        if (!this.notePopover.date || !this.noteTitle || !this.defaultCompanyId) return;

        const md = new Metadata();
        const entry = await md.GetEntityObject<marketingContentCalendarEntity>('Content Calendars');
        entry.NewRecord();
        entry.CompanyID = this.defaultCompanyId;
        entry.Title = this.noteTitle;
        entry.Notes = this.noteText || null;
        entry.PlannedDate = this.notePopover.date;

        // Set content type if selected, otherwise default to first type
        if (this.noteContentTypeId) {
            entry.ContentTypeID = this.noteContentTypeId;
        } else if (this.contentTypes.length > 0) {
            entry.ContentTypeID = this.contentTypes[0].ID;
        }

        // Set status to Draft
        const draftStatus = this.contentStatuses.find(s => s.Name === 'Draft');
        if (draftStatus) {
            entry.ContentStatusID = draftStatus.ID;
        }

        const result = await entry.Save();
        if (result) {
            this.calendarEntries = [...this.calendarEntries, entry];
            this.closeNotePopover();
        }
    }

    // ─── Flyout ──────────────────────────────────────

    async openFlyout(blog: marketingBlogEntity): Promise<void> {
        this.flyout = { open: true, blogId: blog.ID, calendarEntryId: null, newsletterId: null, socialPostId: null };
        this.flyoutBlog = blog;
        // Load revisions for the flyout
        try {
            const rv = new RunView();
            const revResult = await rv.RunView({
                EntityName: 'Blog Revisions',
                ExtraFilter: `BlogID='${blog.ID}'`,
                OrderBy: 'RevisionNumber DESC',
                ResultType: 'simple',
            });
            this.blogRevisions = revResult.Success ? revResult.Results : [];
        } catch { this.blogRevisions = []; }
        this.cdr.detectChanges();
    }

    closeFlyout(): void {
        this.flyout = { open: false, blogId: null, calendarEntryId: null, newsletterId: null, socialPostId: null };
        this.flyoutBlog = null;
        this.flyoutNewsletter = null;
        this.flyoutNewsletterBlogs = [];
        this.flyoutSocialPost = null;
        this.blogRevisions = [];
        this.cdr.detectChanges();
    }

    flyoutNewsletter: marketingNewsletterEntity | null = null;
    flyoutSocialPost: marketingSocialPostEntity | null = null;

    openSocialPostFlyout(post: marketingSocialPostEntity): void {
        this.flyout = { open: true, blogId: null, calendarEntryId: null, newsletterId: null, socialPostId: post.ID };
        this.flyoutSocialPost = post;
        this.cdr.detectChanges();
    }

    async onSocialPostStatusChange(event: { postId: string; newStatus: string }): Promise<void> {
        const sp = this.socialPosts.find(p => p.ID === event.postId);
        if (!sp) return;
        const targetStatus = this.contentStatuses.find(s => s.Name === event.newStatus);
        if (targetStatus) {
            sp.ContentStatusID = targetStatus.ID;
            await sp.Save();
        }
        await this.loadAllData();
        const refreshed = this.socialPosts.find(p => p.ID === event.postId);
        if (refreshed && this.flyout.open) {
            this.flyoutSocialPost = refreshed;
        }
        this.cdr.detectChanges();
    }

    async onSocialPostDelete(postId: string): Promise<void> {
        const sp = this.socialPosts.find(p => p.ID === postId);
        if (!sp) return;
        await sp.Delete();
        this.closeFlyout();
        await this.loadAllData();
        this.cdr.detectChanges();
    }

    async openNewsletterFlyout(newsletter: marketingNewsletterEntity): Promise<void> {
        this.flyout = { open: true, blogId: null, calendarEntryId: null, newsletterId: newsletter.ID, socialPostId: null };
        this.flyoutNewsletter = newsletter;
        // Load linked blogs
        try {
            const rv = new RunView();
            const linkResult = await rv.RunView({
                EntityName: 'Newsletter Blogs',
                ExtraFilter: `NewsletterID='${newsletter.ID}'`,
                OrderBy: 'SortOrder ASC',
                ResultType: 'simple',
            });
            if (linkResult.Success && linkResult.Results.length > 0) {
                const blogIds = linkResult.Results.map((nb: any) => nb.BlogID);
                this.flyoutNewsletterBlogs = this.blogs.filter(b => blogIds.includes(b.ID));
            } else {
                this.flyoutNewsletterBlogs = [];
            }
        } catch { this.flyoutNewsletterBlogs = []; }
        this.cdr.detectChanges();
    }

    async onNewsletterStatusChange(event: { newsletterId: string; newStatus: string }): Promise<void> {
        const nl = this.newsletters.find(n => n.ID === event.newsletterId);
        if (!nl) return;
        const targetStatus = this.contentStatuses.find(s => s.Name === event.newStatus);
        if (targetStatus) {
            nl.ContentStatusID = targetStatus.ID;
            await nl.Save();
        }
        await this.loadAllData();
        const refreshed = this.newsletters.find(n => n.ID === event.newsletterId);
        if (refreshed && this.flyout.open) {
            this.flyoutNewsletter = refreshed;
        }
        this.cdr.detectChanges();
    }

    openBlogById(blogId: string): void {
        const blog = this.blogs.find(b => b.ID === blogId);
        if (blog) this.openFlyout(blog);
    }

    async onNewsletterDelete(newsletterId: string): Promise<void> {
        const nl = this.newsletters.find(n => n.ID === newsletterId);
        if (!nl) return;
        const result = await nl.Delete();
        if (result) {
            this.newsletters = this.newsletters.filter(n => n.ID !== newsletterId);
            this.closeFlyout();
        }
    }

    /** Handle Approve / Publish / Archive status changes from the flyout */
    async onFlyoutStatusChange(event: { blogId: string; newStatus: string }): Promise<void> {
        const blog = this.blogs.find(b => b.ID === event.blogId);
        if (!blog) return;

        if (event.newStatus === 'Published') {
            // Use the PublishToCMS action
            await this.publishBlogViaAction(blog);
        } else {
            // For Ready (approve) / Draft / Archived — update status directly
            const targetStatus = this.blogStatuses.find(s => s.Name === event.newStatus);
            if (targetStatus) {
                blog.StatusID = targetStatus.ID;
                // Save() returns false rather than throwing. Dropping that boolean made a
                // failed approve look exactly like a successful one from the user's side.
                if (!await blog.Save()) {
                    const msg = blog.LatestResult?.Message || 'Save returned false with no error message';
                    console.error('[onFlyoutStatusChange] Status save failed:', msg, blog.LatestResult);
                    alert(`Failed to update status: ${msg}`);
                }
            } else {
                console.error(`[onFlyoutStatusChange] No blog status named "${event.newStatus}"`);
                alert(`Cannot set status "${event.newStatus}" — that status is not configured.`);
            }
        }
        // Refresh data
        await this.loadAllData();
        // Re-open flyout with fresh blog data
        const refreshed = this.blogs.find(b => b.ID === event.blogId);
        if (refreshed && this.flyout.open) {
            this.flyoutBlog = refreshed;
        }
        this.cdr.detectChanges();
    }

    async onFlyoutDeleteBlog(blogId: string): Promise<void> {
        const blog = this.blogs.find(b => b.ID === blogId);
        if (!blog) {
            console.error('[onFlyoutDeleteBlog] Blog not found in local state:', blogId);
            return;
        }
        console.log('[onFlyoutDeleteBlog] Deleting blog', blogId, blog.Title);
        try {
            const result = await blog.Delete();
            if (result) {
                console.log('[onFlyoutDeleteBlog] Delete succeeded');
                this.blogs = this.blogs.filter(b => b.ID !== blogId);
                this.closeFlyout();
                return;
            }
            const msg = blog.LatestResult?.Message || 'Delete returned false with no error message';
            console.error('[onFlyoutDeleteBlog] Delete failed:', msg, blog.LatestResult);
            alert(`Failed to delete blog: ${msg}`);
        } catch (error) {
            console.error('[onFlyoutDeleteBlog] Delete threw:', error);
            alert(`Failed to delete blog: ${error instanceof Error ? error.message : 'Unknown error'}`);
        }
    }

    onFlyoutBlogSaved(_blogId: string): void {
        this.cdr.detectChanges();
    }

    /** Handle revision requests — calls the Revise Blog Post action */
    async onFlyoutRevisionRequested(event: { blogId: string; feedback: string; feedbackOnly: boolean; critiqueType: string }): Promise<void> {
        try {
            const result = await this.runAction('Revise Blog Post', [
                { Name: 'BlogID', Value: event.blogId },
                { Name: 'CompanyID', Value: this.defaultCompanyId },
                { Name: 'Feedback', Value: event.feedback },
                { Name: 'CritiqueType', Value: event.critiqueType },
                { Name: 'FeedbackOnly', Value: String(event.feedbackOnly) },
            ]);
            this.notifyRevisionResult(result.success, result.success ? undefined : result.message);
        } catch (err: any) {
            this.notifyRevisionResult(false, err?.message || 'Revision request failed');
        }
    }

    /** Publish a blog via the PublishToCMS action. Returns success status. */
    private async publishBlogViaAction(blog: marketingBlogEntity): Promise<boolean> {
        const result = await this.runAction('Publish To CMS', [
            { Name: 'BlogID', Value: blog.ID },
            { Name: 'CompanyID', Value: this.defaultCompanyId },
            { Name: 'Status', Value: 'draft' },
        ]);
        if (!result.success) {
            alert(`Publish failed: ${result.message}`);
        }
        return result.success;
    }

    /** Run an MJ Action via GraphQL using the correct RunActionInput schema */
    private async runAction(actionName: string, params: { Name: string; Value: string }[]): Promise<{ success: boolean; message: string; resultCode: string }> {
        try {
            const gql = Metadata.Provider as GraphQLDataProvider;
            const rv = new RunView();
            const actionResult = await rv.RunView({
                EntityName: 'MJ: Actions',
                ExtraFilter: `Name='${actionName}'`,
                MaxRows: 1,
            });
            if (!actionResult.Success || !actionResult.Results?.length) {
                return { success: false, message: `Action '${actionName}' not found`, resultCode: 'NOT_FOUND' };
            }
            const actionId = actionResult.Results[0].ID;
            const result = await gql.ExecuteGQL(
                `mutation RunAction($input: RunActionInput!) {
                    RunAction(input: $input) { Success Message ResultCode }
                }`,
                {
                    input: {
                        ActionID: actionId,
                        Params: params.map(p => ({ Name: p.Name, Value: p.Value, Type: 'Input' })),
                    },
                }
            );
            const actionResponse = result?.RunAction;
            return {
                success: !!actionResponse?.Success,
                message: actionResponse?.Message || 'Unknown error',
                resultCode: actionResponse?.ResultCode || 'UNKNOWN',
            };
        } catch (err: any) {
            return { success: false, message: err?.message || 'Action execution failed', resultCode: 'ERROR' };
        }
    }

    // ─── Scheduled Jobs ──────────────────────────────

    /** Trigger a scheduled job to run immediately (bypassing its cron schedule). */
    async runScheduledJob(job: MarketingScheduledJob): Promise<void> {
        if (this.jobRunState[job.ID] === 'running') return;
        if (job.Status !== 'Active') {
            this.jobRunState[job.ID] = 'error';
            this.jobRunMessage[job.ID] = `Job is ${job.Status}; only Active jobs can be triggered.`;
            this.cdr.detectChanges();
            return;
        }
        this.jobRunState[job.ID] = 'running';
        this.jobRunMessage[job.ID] = '';
        this.cdr.detectChanges();
        // Call our CDP-side "Run Scheduled Job Now" action, not the upstream
        // @memberjunction/scheduling-actions "Execute Scheduled Job Now" —
        // that one is a no-op (inserts a ScheduledJobRun row with
        // Status='Running' and returns without dispatching to the engine).
        // Ours calls SchedulingEngine.Instance.ExecuteScheduledJob — the
        // same code path the cron poller uses — fire-and-forget.
        const result = await this.runAction('Run Scheduled Job Now', [
            { Name: 'JobID', Value: job.ID },
        ]);
        if (result.success) {
            this.jobRunState[job.ID] = 'queued';
            this.jobRunMessage[job.ID] = result.message || 'Dispatched for immediate execution.';
        } else {
            this.jobRunState[job.ID] = 'error';
            this.jobRunMessage[job.ID] = result.message || 'Failed to trigger job.';
        }
        this.cdr.detectChanges();
    }

    /** Refresh the marketing scheduled jobs list (re-runs the full data load). */
    async refreshScheduledJobs(): Promise<void> {
        await this.loadAllData();
    }

    formatJobDate(date: Date | string | null | undefined): string {
        if (!date) return '—';
        const d = new Date(date);
        if (isNaN(d.getTime())) return '—';
        return d.toLocaleString();
    }

    /** Notify the blog detail panel of revision completion */
    private notifyRevisionResult(success: boolean, error?: string): void {
        // The BlogDetailPanelComponent exposes onRevisionComplete() as a public method
        // We access it indirectly by reloading the blog data
        if (success && this.flyoutBlog) {
            this.flyoutBlog.Load(this.flyoutBlog.ID).then(() => {
                this.cdr.detectChanges();
            });
        }
    }

    // ─── Review Queue Helpers ────────────────────────

    isBlogDraft(blog: marketingBlogEntity): boolean {
        return this.getBlogStatusName(blog.StatusID).toLowerCase() === 'draft';
    }

    isBlogReady(blog: marketingBlogEntity): boolean {
        return this.getBlogStatusName(blog.StatusID).toLowerCase() === 'ready';
    }

    getCalendarDateForBlog(blog: marketingBlogEntity): any {
        const entry = this.calendarEntries.find(e => e.BlogID === blog.ID);
        return entry?.PlannedDate;
    }

    async approveBlog(blog: marketingBlogEntity): Promise<void> {
        const readyStatus = this.blogStatuses.find(s => s.Name === 'Ready');
        if (!readyStatus) {
            console.error('[approveBlog] No blog status named "Ready" is configured');
            alert('Cannot approve: the "Ready" status is not configured.');
            return;
        }
        blog.StatusID = readyStatus.ID;
        // Same swallow the flyout path had: Save() returns false instead of throwing, so
        // a rejected approve looked exactly like a successful one.
        if (!await blog.Save()) {
            const msg = blog.LatestResult?.Message || 'Save returned false with no error message';
            console.error('[approveBlog] Save failed:', msg, blog.LatestResult);
            alert(`Failed to approve: ${msg}`);
        }
        this.cdr.detectChanges();
    }

    async publishBlog(blog: marketingBlogEntity): Promise<void> {
        await this.publishBlogViaAction(blog);
        await this.loadAllData();
    }

    async deleteContentSource(cs: marketingContentSourceEntity): Promise<void> {
        if (!confirm(`Delete content source "${cs.Title || 'Untitled'}"? This cannot be undone.`)) return;
        const result = await cs.Delete();
        if (result) {
            this.contentSources = this.contentSources.filter(s => s.ID !== cs.ID);
            this.cdr.detectChanges();
        }
    }

    get unprocessedSourceCount(): number {
        return this.contentSources.filter(s => !s.IsProcessed).length;
    }

    // ─── Agent Sidebar Events ────────────────────────

    onRunRequested(instructions: string): void {
        this.runCMO(instructions);
    }

    // ─── Studio (Pipeline Composer) Events ──────────

    async onStudioAgentCompleted(_event: { outputType: string }): Promise<void> {
        await this.loadAllData();
    }

    onStudioViewChange(event: { view: string; filter?: string }): void {
        if (event.view === 'review-queue') {
            this.activeView = 'review-queue';
            if (event.filter) {
                this.reviewFilter = event.filter as any;
            }
            this.cdr.detectChanges();
        }
    }

    onSidebarCollapse(collapsed: boolean): void {
        this.sidebarCollapsed = collapsed;
        localStorage.setItem('cmo-sidebar-collapsed', String(collapsed));
        this.cdr.detectChanges();
    }

    // ─── Run CMO Agent ───────────────────────────────

    async runCMO(instructions?: string): Promise<void> {
        if (this.isRunning || !this.defaultCompanyId) return;

        this.isRunning = true;
        this.enhancedSteps = [];
        this.runOutputBlogs = [];
        this.agentStartTime = Date.now();

        // Ensure sidebar is visible
        if (this.sidebarCollapsed) {
            this.sidebarCollapsed = false;
            localStorage.setItem('cmo-sidebar-collapsed', 'false');
        }

        this.addEnhancedStep('Prompt', 'Starting CMO Agent', 'Running', 'Initializing...');

        try {
            const gql = Metadata.Provider as GraphQLDataProvider;
            const rv = new RunView();
            const agentResult = await rv.RunView({
                EntityName: 'MJ: AI Agents', ExtraFilter: `Name='Marketing CMO Agent'`, MaxRows: 1,
            });

            if (!agentResult?.Success || !agentResult.Results?.length) {
                this.addEnhancedStep('Actions', 'Agent not found', 'Failed', 'Marketing CMO Agent not found. Has metadata been synced?');
                this.isRunning = false;
                return;
            }

            this.cmoAgentId = agentResult.Results[0].ID;
            let userMessage = instructions || 'Assess the current marketing state and take one high-impact action.';
            userMessage += `\n\nCompany: ${this.getCompanyName()} (CompanyID: ${this.defaultCompanyId})`;

            const result = await gql.ExecuteGQL(
                `mutation RunAIAgent($agentId: String!, $sessionId: String!, $messages: String!, $payload: String, $fireAndForget: Boolean) {
                    RunAIAgent(agentId: $agentId, sessionId: $sessionId, messages: $messages, payload: $payload, fireAndForget: $fireAndForget) { success errorMessage result }
                }`,
                {
                    agentId: this.cmoAgentId,
                    sessionId: 'cmo-dashboard-' + Date.now(),
                    messages: JSON.stringify([{ role: 'user', content: userMessage }]),
                    payload: JSON.stringify({ companyId: this.defaultCompanyId, ...(instructions ? { instructions } : {}) }),
                    fireAndForget: true,
                }
            );

            if (result?.RunAIAgent?.success) {
                this.addEnhancedStep('Prompt', 'CMO Agent started', 'Running', 'Assessing marketing health...');
                this.startPolling();
            } else {
                this.addEnhancedStep('Actions', 'Failed to start', 'Failed', result?.RunAIAgent?.errorMessage || 'Unknown error');
                this.isRunning = false;
            }
        } catch (err: any) {
            this.addEnhancedStep('Actions', 'Error', 'Failed', err?.message || 'Failed to run CMO agent');
            this.isRunning = false;
        }
        this.cdr.detectChanges();
    }

    // ─── Polling ─────────────────────────────────────

    private startPolling(): void {
        this.stopPolling();
        let pollCount = 0;

        this.pollingTimer = setInterval(async () => {
            pollCount++;
            try {
                const rv = new RunView();
                const runResult = await rv.RunView({
                    EntityName: 'MJ: AI Agent Runs',
                    ExtraFilter: `AgentID='${this.cmoAgentId}' AND StartedAt >= '${new Date(this.agentStartTime - 5000).toISOString()}'`,
                    OrderBy: 'StartedAt DESC', MaxRows: 1, ResultType: 'simple',
                });

                if (runResult.Success && runResult.Results?.length > 0) {
                    const run = runResult.Results[0] as Record<string, any>;
                    this.currentRunId = run['ID'] as string;
                    await this.fetchRunSteps();

                    const status = run['Status'] as string;
                    if (status === 'Completed') {
                        this.addEnhancedStep('Prompt', 'Agent completed', 'Completed',
                            `Finished in ${this.formatElapsed(Date.now() - this.agentStartTime)}`);
                        this.stopPolling();
                        this.isRunning = false;
                        await this.loadRunOutput();
                        await this.loadAllData();
                    } else if (status === 'Failed' || status === 'Cancelled') {
                        this.addEnhancedStep('Actions', `Agent ${status.toLowerCase()}`, 'Failed',
                            run['ErrorMessage'] || 'Run failed');
                        this.stopPolling();
                        this.isRunning = false;
                    }
                }
                if (pollCount >= 120) {
                    this.addEnhancedStep('Actions', 'Timeout', 'Failed', 'Agent did not complete within 10 minutes');
                    this.stopPolling();
                    this.isRunning = false;
                }
            } catch (err) { console.error('Polling error:', err); }
            this.cdr.detectChanges();
        }, 5000);
    }

    private async fetchRunSteps(): Promise<void> {
        if (!this.currentRunId) return;
        try {
            const rv = new RunView();
            const stepsResult = await rv.RunView({
                EntityName: 'MJ: AI Agent Run Steps',
                ExtraFilter: `AgentRunID='${this.currentRunId}'`,
                OrderBy: 'StepNumber ASC', ResultType: 'simple',
            });

            if (stepsResult.Success) {
                const existingIds = new Set(this.enhancedSteps.map(s => s.id));

                for (const step of stepsResult.Results as Record<string, any>[]) {
                    const id = step['ID'] as string;
                    if (existingIds.has(id)) {
                        // Update status of existing step
                        const existing = this.enhancedSteps.find(s => s.id === id);
                        if (existing) {
                            existing.status = step['Status'] || existing.status;
                            if (step['CompletedAt']) existing.completedAt = new Date(step['CompletedAt']);
                            if (step['OutputData']) existing.outputData = step['OutputData'];
                            if (existing.startedAt && existing.completedAt) {
                                existing.duration = this.formatStepDuration(existing.startedAt, existing.completedAt);
                            }
                        }
                        continue;
                    }
                    existingIds.add(id);

                    const stepType = this.normalizeStepType(step['StepType']);
                    const stepName = step['StepName'] || step['Name'] || `Step ${step['StepNumber']}`;
                    const started = step['StartedAt'] ? new Date(step['StartedAt']) : null;
                    const ended = step['CompletedAt'] ? new Date(step['CompletedAt']) : null;
                    const duration = started && ended ? this.formatStepDuration(started, ended) : null;

                    this.enhancedSteps.push({
                        id,
                        stepNumber: step['StepNumber'] || 0,
                        stepType,
                        stepName,
                        status: step['Status'] || 'Running',
                        startedAt: started,
                        completedAt: ended,
                        duration,
                        inputData: step['InputData'] || null,
                        outputData: step['OutputData'] || null,
                        errorMessage: step['ErrorMessage'] || null,
                        targetId: step['TargetID'] || null,
                        targetLogId: step['TargetLogID'] || null,
                        expanded: false,
                        childSteps: [],
                        childrenLoaded: false,
                    });
                }

                // Load child steps for Sub-Agent steps
                for (const s of this.enhancedSteps) {
                    if (s.stepType === 'Sub-Agent' && !s.childrenLoaded && s.targetLogId) {
                        await this.loadChildSteps(s);
                    }
                }
            }
        } catch { /* non-fatal */ }
    }

    private async loadChildSteps(parentStep: AgentStepEnhanced): Promise<void> {
        if (!parentStep.targetLogId) return;
        try {
            const rv = new RunView();
            const childResult = await rv.RunView({
                EntityName: 'MJ: AI Agent Run Steps',
                ExtraFilter: `AgentRunID='${parentStep.targetLogId}'`,
                OrderBy: 'StepNumber ASC', ResultType: 'simple',
            });

            if (childResult.Success) {
                parentStep.childSteps = (childResult.Results as Record<string, any>[]).map((step) => {
                    const started = step['StartedAt'] ? new Date(step['StartedAt']) : null;
                    const ended = step['CompletedAt'] ? new Date(step['CompletedAt']) : null;
                    return {
                        id: step['ID'],
                        stepNumber: step['StepNumber'] || 0,
                        stepType: this.normalizeStepType(step['StepType']),
                        stepName: step['StepName'] || `Step ${step['StepNumber']}`,
                        status: step['Status'] || 'Running',
                        startedAt: started,
                        completedAt: ended,
                        duration: started && ended ? this.formatStepDuration(started, ended) : null,
                        inputData: step['InputData'] || null,
                        outputData: step['OutputData'] || null,
                        errorMessage: step['ErrorMessage'] || null,
                        targetId: step['TargetID'] || null,
                        targetLogId: step['TargetLogID'] || null,
                        expanded: false,
                        childSteps: [],
                        childrenLoaded: true,
                    };
                });
                parentStep.childrenLoaded = true;
            }
        } catch { /* non-fatal */ }
    }

    private normalizeStepType(raw: string): AgentStepEnhanced['stepType'] {
        const map: Record<string, AgentStepEnhanced['stepType']> = {
            'actions': 'Actions', 'action': 'Actions',
            'chat': 'Chat',
            'decision': 'Decision',
            'foreach': 'ForEach', 'for each': 'ForEach',
            'prompt': 'Prompt',
            'sub-agent': 'Sub-Agent', 'subagent': 'Sub-Agent',
            'validation': 'Validation',
            'while': 'While',
        };
        return map[(raw || '').toLowerCase()] || 'Actions';
    }

    private addEnhancedStep(type: AgentStepEnhanced['stepType'], name: string, status: AgentStepEnhanced['status'], detail?: string): void {
        this.enhancedSteps.push({
            id: 'local-' + Date.now() + '-' + Math.random(),
            stepNumber: this.enhancedSteps.length + 1,
            stepType: type,
            stepName: name,
            status,
            startedAt: new Date(),
            completedAt: status === 'Completed' || status === 'Failed' ? new Date() : null,
            duration: null,
            inputData: null,
            outputData: detail || null,
            errorMessage: status === 'Failed' ? detail || null : null,
            targetId: null,
            targetLogId: null,
            expanded: false,
            childSteps: [],
            childrenLoaded: true,
        });
        this.cdr.detectChanges();
    }

    private async loadRunOutput(): Promise<void> {
        if (!this.defaultCompanyId || !this.agentStartTime) return;
        try {
            const rv = new RunView();
            const result = await rv.RunView<marketingBlogEntity>({
                EntityName: 'Blogs',
                ExtraFilter: `CompanyID='${this.defaultCompanyId}' AND __mj_CreatedAt >= '${new Date(this.agentStartTime - 5000).toISOString()}'`,
                OrderBy: '__mj_CreatedAt DESC', ResultType: 'entity_object',
            });
            if (result.Success) this.runOutputBlogs = result.Results || [];
        } catch (err) { console.error('Failed to load run output:', err); }
        this.cdr.detectChanges();
    }

    private stopPolling(): void {
        if (this.pollingTimer) { clearInterval(this.pollingTimer); this.pollingTimer = null; }
    }

    // ─── Helpers ─────────────────────────────────────

    getCompanyName(): string {
        return this.companies.find((c: any) => c.ID === this.defaultCompanyId)?.Name || 'Unknown';
    }

    getBlogStatusName(statusId: string): string {
        return this.blogStatuses.find(s => s.ID === statusId)?.Name || 'Unknown';
    }

    getContentTypeName(typeId: string): string {
        return this.contentTypes.find(t => t.ID === typeId)?.Name || 'Unknown';
    }

    getContentStatusName(statusId: string): string {
        return this.contentStatuses.find(s => s.ID === statusId)?.Name || 'Unknown';
    }

    formatDate(date: any): string {
        if (!date) return '\u2014';
        return new Date(date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    }

    formatDateTime(date: any): string {
        if (!date) return '\u2014';
        return new Date(date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
    }

    private formatElapsed(ms: number): string {
        const sec = Math.floor(ms / 1000);
        return `${Math.floor(sec / 60)}:${(sec % 60).toString().padStart(2, '0')}`;
    }

    private formatStepDuration(start: Date, end: Date): string {
        const ms = end.getTime() - start.getTime();
        return ms < 1000 ? `${ms}ms` : `${(ms / 1000).toFixed(1)}s`;
    }

    getElapsed(): string {
        return this.agentStartTime ? this.formatElapsed(Date.now() - this.agentStartTime) : '0:00';
    }

    // ─── Save Operations ─────────────────────────────

    async saveEntity(entity: any, sectionKey: string): Promise<void> {
        this.saveStatus[sectionKey] = 'saving';
        this.cdr.detectChanges();
        try {
            const result = await entity.Save();
            this.saveStatus[sectionKey] = result ? 'saved' : 'error';
            if (result) {
                setTimeout(() => { this.saveStatus[sectionKey] = 'idle'; this.cdr.detectChanges(); }, 2000);
            }
        } catch (err: any) {
            console.error(`Save failed for ${sectionKey}:`, err);
            this.saveStatus[sectionKey] = 'error';
        }
        this.cdr.detectChanges();
    }

    async saveBrandConfig(): Promise<void> {
        if (this.brandConfigRaw) await this.saveEntity(this.brandConfigRaw, 'brandConfig');
    }

    async saveChannelConfig(config: marketingChannelConfigurationEntity): Promise<void> {
        await this.saveEntity(config, 'channel-' + config.ID);
    }

    async saveStrategy(): Promise<void> {
        if (this.strategy) await this.saveEntity(this.strategy, 'strategy');
    }

    async savePillar(pillar: marketingContentStrategyPillarEntity): Promise<void> {
        await this.saveEntity(pillar, 'pillar-' + pillar.ID);
    }

    async addPillar(): Promise<void> {
        if (!this.strategy) return;
        const md = new Metadata();
        const pillar = await md.GetEntityObject<marketingContentStrategyPillarEntity>('Content Strategy Pillars');
        pillar.NewRecord();
        pillar.ContentStrategyID = this.strategy.ID;
        pillar.Name = '';
        pillar.Description = '';
        pillar.Priority = this.strategyPillars.length + 1;
        this.strategyPillars.push(pillar);
        this.cdr.detectChanges();
    }

    async deletePillar(pillar: marketingContentStrategyPillarEntity, index: number): Promise<void> {
        if (!confirm(`Delete pillar "${pillar.Name || 'Untitled'}"?`)) return;
        if (pillar.IsSaved) {
            const result = await pillar.Delete();
            if (!result) return;
        }
        this.strategyPillars.splice(index, 1);
        this.cdr.detectChanges();
    }

    async savePlanItem(item: marketingContentPlanItemEntity): Promise<void> {
        await this.saveEntity(item, 'planItem-' + item.ID);
    }

    async saveFeed(feed: marketingContentFeedEntity): Promise<void> {
        await this.saveEntity(feed, 'feed-' + feed.ID);
    }

    async saveTemplate(template: marketingContentTemplateEntity): Promise<void> {
        await this.saveEntity(template, 'template-' + template.ID);
    }

    // ─── Channel Rules ───────────────────────────────

    async onAddChannelConfig(data: { contentTypeId: string; socialPlatformId: string | null; requiresApproval: boolean; autoPublish: boolean; maxPerWeek: number | null }): Promise<void> {
        if (!this.defaultCompanyId) return;
        try {
            const md = new Metadata();
            const entity = await md.GetEntityObject<marketingChannelConfigurationEntity>('Channel Configurations');
            entity.NewRecord();
            entity.Set('CompanyID', this.defaultCompanyId);
            entity.Set('ContentTypeID', data.contentTypeId);
            if (data.socialPlatformId) entity.Set('SocialPlatformID', data.socialPlatformId);
            entity.Set('RequiresApproval', data.requiresApproval);
            entity.Set('AutoPublish', data.autoPublish);
            if (data.maxPerWeek != null) entity.Set('MaxPerWeek', data.maxPerWeek);
            const saved = await entity.Save();
            if (saved) {
                this.channelConfigs = [...this.channelConfigs, entity];
                this.cdr.detectChanges();
            }
        } catch (err) {
            console.error('Failed to add channel config', err);
        }
    }

    async onDeleteChannelConfig(id: string): Promise<void> {
        if (!confirm('Delete this channel rule?')) return;
        const target = this.channelConfigs.find(c => c.ID === id);
        if (!target) return;
        const result = await target.Delete();
        if (result) {
            this.channelConfigs = this.channelConfigs.filter(c => c.ID !== id);
            this.cdr.detectChanges();
        }
    }

    // ─── Company Integrations ────────────────────────

    async onAddCompanyIntegration(data: { integrationId: string; accessToken?: string; apiKey?: string }): Promise<void> {
        if (!this.defaultCompanyId) return;
        try {
            const md = new Metadata();
            const entity = await md.GetEntityObject<MJCompanyIntegrationEntity>('MJ: Company Integrations');
            entity.NewRecord();
            entity.Set('CompanyID', this.defaultCompanyId);
            entity.Set('IntegrationID', data.integrationId);
            entity.Set('IsActive', true);
            entity.Set('IsExternalSystemReadOnly', false);
            entity.Set('ScheduleEnabled', false);
            entity.Set('ScheduleType', 'Manual');
            if (data.accessToken) entity.Set('AccessToken', data.accessToken);
            if (data.apiKey) entity.Set('APIKey', data.apiKey);
            const saved = await entity.Save();
            if (saved) await this.loadAllData();
        } catch (err) {
            console.error('Failed to add company integration', err);
        }
    }

    async onToggleCompanyIntegration(ciId: string): Promise<void> {
        const ci = this.companyIntegrations.find(c => c.ID === ciId);
        if (!ci) return;
        try {
            const rv = new RunView();
            const result = await rv.RunView({
                EntityName: 'MJ: Company Integrations',
                ExtraFilter: `ID='${ciId}'`,
                ResultType: 'entity_object',
            });
            if (!result.Success || !result.Results?.length) return;
            const entity = result.Results[0];
            entity.Set('IsActive', !ci.IsActive);
            const saved = await entity.Save();
            if (!saved) {
                alert('Failed to toggle integration: ' + (entity.LatestResult?.Message || 'Unknown error'));
                return;
            }
            await this.loadAllData();
        } catch (err) {
            console.error('Failed to toggle company integration', err);
        }
    }

    async onDeleteCompanyIntegration(ciId: string): Promise<void> {
        if (!confirm('Disconnect this integration? Stored credentials will be removed.')) return;
        try {
            const rv = new RunView();
            const result = await rv.RunView({
                EntityName: 'MJ: Company Integrations',
                ExtraFilter: `ID='${ciId}'`,
                ResultType: 'entity_object',
            });
            if (!result.Success || !result.Results?.length) return;
            const deleted = await result.Results[0].Delete();
            if (!deleted) {
                alert('Failed to delete integration: ' + (result.Results[0].LatestResult?.Message || 'Unknown error'));
                return;
            }
            await this.loadAllData();
        } catch (err) {
            console.error('Failed to delete company integration', err);
        }
    }

    async onCreateTemplate(event: { name: string; templateType: string }): Promise<void> {
        try {
            const md = new Metadata();
            const template = await md.GetEntityObject<marketingContentTemplateEntity>('Content Templates');
            template.NewRecord();
            template.Set('CompanyID', this.defaultCompanyId);
            template.Set('Name', event.name);
            template.Set('TemplateType', event.templateType);
            template.Set('TemplateBody', '');
            template.Set('IsActive', true);
            const saved = await template.Save();
            if (saved) {
                this.contentTemplates = [...this.contentTemplates, template];
                this.cdr.detectChanges();
            }
        } catch (err) {
            console.error('Failed to create template', err);
        }
    }

    getSaveLabel(key: string): string {
        const s = this.saveStatus[key];
        if (s === 'saving') return 'Saving...';
        if (s === 'saved') return 'Saved';
        if (s === 'error') return 'Error — retry';
        return 'Save';
    }
}

export function LoadCMODashboard() {}
